/**
 * Frontend name filtering utility to prevent inappropriate names in user profiles
 * This provides client-side validation for better UX, but backend validation is still required
 */

// List of banned words/phrases (case-insensitive)
const BANNED_WORDS = [
  // Profanity
  'fuck', 'fucking', 'shit', 'damn', 'bitch', 'bastard', 'asshole', 'ass',
  'crap', 'piss', 'cock', 'dick', 'pussy', 'cunt', 'whore', 'slut',
  'motherfucker', 'fag', 'faggot', 'nigger', 'nigga', 'retard', 'retarded',
  
  // Offensive terms
  'nazi', 'hitler', 'terrorist', 'jihad', 'isis', 'kkk', 'genocide',
  
  // Sexual content
  'porn', 'sex', 'xxx', 'nude', 'naked', 'horny', 'orgasm', 'masturbate',
  
  // Drugs
  'cocaine', 'heroin', 'meth', 'weed', 'marijuana', 'cannabis', 'drug dealer',
  
  // Violence
  'kill', 'murder', 'suicide', 'bomb', 'terrorist', 'violence', 'rape',
  
  // Spam/inappropriate
  'admin', 'administrator', 'moderator', 'support', 'official', 'staff',
  'nvgtr', 'navigator', 'system', 'bot', 'ai', 'artificial intelligence',
  
  // Common variations and leetspeak
  'f*ck', 'f**k', 'sh*t', 'sh**', 'b*tch', 'a**hole', 'a$$hole',
  'fuk', 'fck', 'sht', 'btch', 'azz', 'asz', 'fag0t', 'n1gger',
  
  // Additional inappropriate terms
  'pedo', 'pedophile', 'molest', 'abuse', 'hate', 'racist', 'sexist'
];

// List of suspicious patterns (regex patterns)
const SUSPICIOUS_PATTERNS = [
  /(.)\1{4,}/i, // Repeated characters (5 or more)
  /^[^a-zA-Z]*$/i, // Only numbers/symbols
  /^\d+$/i, // Only numbers
  /^.{1,2}$/i, // Too short (1-2 characters)
  /^.{51,}$/i, // Too long (over 50 characters)
  /[!@#$%^&*()_+=\[\]{}|;':",./<>?~`]{3,}/i, // Too many special characters in a row
  /^\s*$/, // Only whitespace
];

/**
 * Check if a name contains banned words or inappropriate content
 * @param {string} name - The name to check
 * @returns {Object} - { isValid: boolean, reason: string }
 */
export function validateName(name) {
  if (!name || typeof name !== 'string') {
    return { isValid: false, reason: 'Name is required and must be a string' };
  }

  const trimmedName = name.trim();
  
  if (trimmedName.length === 0) {
    return { isValid: false, reason: 'Name cannot be empty' };
  }

  if (trimmedName.length < 2) {
    return { isValid: false, reason: 'Name must be at least 2 characters long' };
  }

  if (trimmedName.length > 50) {
    return { isValid: false, reason: 'Name cannot be longer than 50 characters' };
  }

  // Check for suspicious patterns
  for (const pattern of SUSPICIOUS_PATTERNS) {
    if (pattern.test(trimmedName)) {
      return { isValid: false, reason: 'Name contains inappropriate patterns or formatting' };
    }
  }

  // Check for banned words
  const lowerName = trimmedName.toLowerCase();
  for (const bannedWord of BANNED_WORDS) {
    if (lowerName.includes(bannedWord.toLowerCase())) {
      return { isValid: false, reason: 'Name contains inappropriate language' };
    }
  }

  // Check for variations with numbers/symbols replacing letters
  const normalizedName = lowerName
    .replace(/[0]/g, 'o')
    .replace(/[1]/g, 'i')
    .replace(/[3]/g, 'e')
    .replace(/[4]/g, 'a')
    .replace(/[5]/g, 's')
    .replace(/[7]/g, 't')
    .replace(/[@]/g, 'a')
    .replace(/[\$]/g, 's')
    .replace(/[!]/g, 'i');

  for (const bannedWord of BANNED_WORDS) {
    if (normalizedName.includes(bannedWord.toLowerCase())) {
      return { isValid: false, reason: 'Name contains inappropriate language' };
    }
  }

  return { isValid: true, reason: null };
}

/**
 * Validate full name (firstName + lastName combination)
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @returns {Object} - { isValid: boolean, reason: string }
 */
export function validateFullName(firstName, lastName) {
  // Validate individual parts
  const firstNameValidation = validateName(firstName);
  if (!firstNameValidation.isValid) {
    return { isValid: false, reason: `First name: ${firstNameValidation.reason}` };
  }

  const lastNameValidation = validateName(lastName);
  if (!lastNameValidation.isValid) {
    return { isValid: false, reason: `Last name: ${lastNameValidation.reason}` };
  }

  // Validate combined name
  const fullName = `${firstName.trim()} ${lastName.trim()}`;
  const fullNameValidation = validateName(fullName);
  if (!fullNameValidation.isValid) {
    return { isValid: false, reason: `Full name: ${fullNameValidation.reason}` };
  }

  return { isValid: true, reason: null };
}

/**
 * Validate preferred/call name
 * @param {string} preferredName - Preferred name
 * @returns {Object} - { isValid: boolean, reason: string }
 */
export function validatePreferredName(preferredName) {
  return validateName(preferredName);
}

/**
 * Get a sanitized version of a name (removes/replaces inappropriate content)
 * This is a fallback - we should reject rather than sanitize, but this can be useful for display
 * @param {string} name - The name to sanitize
 * @returns {string} - Sanitized name
 */
export function sanitizeName(name) {
  if (!name || typeof name !== 'string') {
    return '';
  }

  let sanitized = name.trim();
  
  // Remove banned words (replace with asterisks)
  const lowerName = sanitized.toLowerCase();
  for (const bannedWord of BANNED_WORDS) {
    const regex = new RegExp(bannedWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    sanitized = sanitized.replace(regex, '*'.repeat(bannedWord.length));
  }

  return sanitized;
}


















